import { combineReducers, configureStore, createSlice } from "@reduxjs/toolkit";
import {
  persistReducer,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { prepareCartProduct } from "../utils/productType";
// ACCESSING LOCALSTORAGE *******
// const userData = useSelector(
//   (state) => state.userData.userData
// );

// This file holds redux functions for localUserData and the cart

const initialUserDataState = {
  userData: {},
};

const userDataReducer = createSlice({
  name: "userData",
  initialState: initialUserDataState,
  reducers: {
    removeUserData(state) {
      state.userData = {};
    },
    updateUserData(state, action) {
      state.userData = action.payload;
    },
  },
});

const initialCartState = {
  products: [],
  donations: [],
};

const initialWishlistState = {
  wishlists: [],
  loading: false,
  error: null,
};

const cartReducer = createSlice({
  name: "cart",
  initialState: initialCartState,
  reducers: {
    addProductToCart(state, action) {
      if (!state.products) state.products = [];
      const newProduct = action.payload;
      const normalizedProduct = prepareCartProduct(newProduct);

      // Find existing product by product_id, color, and size
      const existingProduct = state.products.find(
        (product) =>
          product.product_id === newProduct.product_id &&
          product?.product_color === newProduct?.product_color &&
          product?.product_size === newProduct?.product_size
      );

      if (existingProduct) {
        // Merge quantities for duplicate products
        existingProduct.quantity =
          Number(existingProduct.quantity) + Number(newProduct.quantity);
        if (!existingProduct.product_type) {
          existingProduct.product_type = normalizedProduct.product_type;
        }
      } else {
        // Push a copy to avoid payload mutation
        state.products.push({ ...normalizedProduct });
      }
    },
    addDonationToCart(state, action) {
      if (!state.donations) state.donations = [];
      const newDonation = action.payload;
      const existingDonation = state.donations.find(
        (donation) => donation.donation_id === newDonation.donation_id
      );
      if (existingDonation) {
        existingDonation.amount = newDonation.amount;
      } else {
        state.donations.push(newDonation);
      }
    },
    removeProductFromCart(state, action) {
      const { product_id, product_size, product_color } = action.payload;
      state.products = state.products.filter(
        (product) =>
          !(
            product.product_id === product_id &&
            product?.product_color === product_color &&
            product?.product_size === product_size
          )
      );
    },
    removeDonationFromCart(state, action) {
      const { donation_id } = action.payload;
      state.donations = state.donations.filter(
        (donation) => donation.donation_id !== donation_id
      );
    },
    updateProductQuantity(state, action) {
      const { product_id, quantity, product_size, product_color } =
        action.payload;

      const existingProduct = state.products.find(
        (product) =>
          product.product_id === product_id &&
          product?.product_color === product_color &&
          product?.product_size === product_size
      );
      if (existingProduct) existingProduct.quantity = Number(quantity);
    },
    updateDonationAmount(state, action) {
      const { donation_id, amount } = action.payload;
      const existingDonation = state.donations.find(
        (donation) => donation.donation_id === donation_id
      );
      if (existingDonation) existingDonation.amount = Number(amount);
    },
    clearCart(state) {
      state.products = [];
      state.donations = [];
    },
  },
});

export const cartActions = cartReducer.actions;

const wishlistReducer = createSlice({
  name: "wishlist",
  initialState: initialWishlistState,
  reducers: {
    setWishlists(state, action) {
      state.wishlists = action.payload;
      state.loading = false;
      state.error = null;
    },
    addWishlist(state, action) {
      state.wishlists.unshift(action.payload);
    },
    updateWishlist(state, action) {
      const index = state.wishlists.findIndex(
        (w) => w.id === action.payload.id
      );
      if (index !== -1) {
        state.wishlists[index] = action.payload;
      }
    },
    removeWishlist(state, action) {
      state.wishlists = state.wishlists.filter((w) => w.id !== action.payload);
    },
    setWishlistLoading(state, action) {
      state.loading = action.payload;
    },
    setWishlistError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearWishlistError(state) {
      state.error = null;
    },
  },
});

export const wishlistActions = wishlistReducer.actions;

const rootReducer = combineReducers({
  userData: userDataReducer.reducer,
  cart: cartReducer.reducer,
  wishlist: wishlistReducer.reducer,
});

const persistConfig = {
  key: "root",
  storage,
  version: 1,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const userDataActions = userDataReducer.actions;
export default store;
export const persistor = persistStore(store);
